import React from 'react';
import TopBar from './TopBar';
import { ELEMENTS } from '../utils/constants';
import { lastNEntries, landscapeForEntry } from '../utils/helpers';
import useDeviceDetection from '../hooks/useDeviceDetection';

function Insights({ state, setRoute }) {
  const recent = lastNEntries(state.entries, 14);
  const latest = recent[0] || null;
  const landscape = latest ? landscapeForEntry(latest) : { earth: [], water: [], air: [], fire: [] };
  const { isMobile } = useDeviceDetection();

  return (
    <div className="p-4">
      <TopBar title={`Insights`} onBack={() => setRoute("home")} />
      <div className="mt-3">
        <div className="rounded-lg bg-white dark:bg-dark-surface p-3 border dark:border-dark-border">
          <div className="text-sm font-medium dark:text-dark-text">Body Map • Latest</div>
          <div className="mt-3 flex gap-3">
            <div className="w-1/2">
              <svg viewBox="0 0 200 400" className="w-full h-auto rounded-md border dark:border-dark-border">
                <rect x="40" y="80" width="120" height="160" rx="30" fill="#f8fafc" stroke="#e5e7eb" className="dark:fill-dark-bg dark:stroke-dark-border" />
                <rect x="40" y="180" width="120" height="60" rx="20" fill={landscape.earth.length ? '#86efac' : '#f3f4f6'} className="dark:fill-dark-surface" />
                <rect x="40" y="120" width="120" height="60" rx="20" fill={landscape.water.length ? '#93c5fd' : '#f3f4f6'} className="dark:fill-dark-surface" />
                <circle cx="100" cy="60" r="28" fill={landscape.air.length ? '#bae6fd' : '#f3f4f6'} className="dark:fill-dark-surface" />
                <circle cx="100" cy="150" r="20" fill={landscape.fire.length ? '#fca5a5' : '#f3f4f6'} className="dark:fill-dark-surface" />
                <text x="100" y="200" textAnchor="middle" fontSize="18">{landscape.earth.length ? '🌱' : ''}</text>
                <text x="100" y="150" textAnchor="middle" fontSize="18">{landscape.water.length ? '💧' : ''}</text>
                <text x="100" y="40" textAnchor="middle" fontSize="18">{landscape.air.length ? '🌬️' : ''}</text>
                <text x="140" y="140" textAnchor="middle" fontSize="18">{landscape.fire.length ? '🔥' : ''}</text>
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-600 dark:text-dark-textSecondary">Element keywords (today)</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {ELEMENTS.map((el) => (
                  <div key={el.key} className="p-2 rounded-md border dark:border-dark-border bg-white dark:bg-dark-bg flex flex-col">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="text-lg">{el.emoji}</div>
                      <div>
                        <div className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium dark:text-dark-text`}>{el.label}</div>
                        <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-dark-textSecondary`}>{landscape[el.key].slice(0,3).join(', ') || '—'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <div className="text-sm font-medium dark:text-dark-text">Landscape preview</div>
                <div className="mt-2 h-28 rounded-md border dark:border-dark-border p-2 bg-white dark:bg-dark-bg flex items-center justify-center">
                  <div className="w-full text-center text-xs text-gray-700 dark:text-dark-text">
                    <div>Earth: {landscape.earth.join(', ') || 'green plains'}</div>
                    <div>Water: {landscape.water.join(', ') || 'still lake'}</div>
                    <div>Air: {landscape.air.join(', ') || 'clear sky'}</div>
                    <div>Fire: {landscape.fire.join(', ') || 'steady flame'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 rounded-lg border dark:border-dark-border bg-white dark:bg-dark-surface">
          <div className="text-sm font-medium dark:text-dark-text">Weekly Round-up (mock AI)</div>
          <div className="text-xs text-gray-500 dark:text-dark-textSecondary mt-2">Generated once per week — shows themes, suggested micro-actions and what worked.</div>
          <div className="mt-3 text-sm text-gray-800 dark:text-dark-text">
            {recent.length === 0 ? (
              <div>No data yet. Start logging to get weekly reflections.</div>
            ) : (
              <div>
                <div className="font-medium">This week:</div>
                <ul className="list-disc ml-5 mt-2 text-sm">
                  <li>Fire: low to medium — music seems to brighten it when mentioned.</li>
                  <li>Earth: stable — snacks are a recurring theme after evenings.</li>
                  <li>Action suggestion: try a 10-min walk after dinner 3x this week.</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {/* Recent Entries */}
        <div className="mt-4 p-3 rounded-lg border dark:border-dark-border bg-white dark:bg-dark-surface">
          <div className="text-sm font-medium dark:text-dark-text">Recent Entries</div>
          <div className="text-xs text-gray-500 dark:text-dark-textSecondary mt-1">Your 3 most recent logged days</div>
          <div className="mt-3 space-y-3">
            {recent.slice(0, 3).map((entry, index) => (
              <div key={entry.date} className="p-3 rounded-md border dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium dark:text-dark-text">
                    {new Date(entry.date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-dark-textSecondary">
                    {index === 0 ? 'Latest' : `${index + 1} days ago`}
                  </div>
                </div>
                <div className={`${isMobile ? 'flex gap-3 overflow-x-auto pb-2' : 'grid grid-cols-2 gap-2'}`}>
                  {ELEMENTS.map((el) => {
                    const elementData = entry.values[el.key];
                    return (
                      <div key={el.key} className={`${isMobile ? 'flex-none w-80' : ''} flex flex-col p-2 rounded border dark:border-dark-border bg-white dark:bg-dark-surface`}>
                        <div className="flex items-start gap-2 flex-1">
                          <div className="text-sm">{el.emoji}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className={`${isMobile ? 'text-sm' : 'text-xs'} font-medium text-gray-700 dark:text-dark-text`}>{el.label}</div>
                              {state.ratingTracking?.enabled && elementData?.rating && (
                                <div className="text-lg">{elementData.rating}</div>
                              )}
                            </div>
                            <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-dark-textSecondary mt-1`}>
                              {elementData?.text ? (
                                <div className="truncate">{elementData.text}</div>
                              ) : (
                                <div className="text-gray-400 dark:text-dark-muted italic">No entry</div>
                              )}
                            </div>
                            {elementData?.action && (
                              <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-blue-600 dark:text-blue-400 mt-1`}>
                                Action: {elementData.action}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {recent.length === 0 && (
              <div className="text-center py-6 text-gray-500 dark:text-dark-textSecondary">
                <div className="text-sm">No entries yet</div>
                <div className="text-xs mt-1">Start logging to see your recent entries here</div>
              </div>
            )}
          </div>
        </div>

        {/* Rating History */}
        {state.ratingTracking?.enabled && (
          <div className="mt-4 p-3 rounded-lg border dark:border-dark-border bg-white dark:bg-dark-surface">
            <div className="text-sm font-medium dark:text-dark-text">Rating Trends</div>
            <div className="text-xs text-gray-500 dark:text-dark-textSecondary mt-1">Track your progress over time</div>
            <div className="mt-3 space-y-4">
              {ELEMENTS.map((el) => {
                const ratingsWithDates = recent
                  .map(entry => ({
                    date: entry.date,
                    rating: entry.values[el.key]?.rating || null
                  }))
                  .filter(item => item.rating !== null)
                  .reverse();

                if (ratingsWithDates.length === 0) {
                  return (
                    <div key={el.key} className="p-3 rounded-md border dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-lg">{el.emoji}</div>
                        <div className="text-sm font-medium dark:text-dark-text">{el.label}</div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-textSecondary">No ratings yet</div>
                    </div>
                  );
                }

                return (
                  <div key={el.key} className="p-3 rounded-md border dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="text-lg">{el.emoji}</div>
                      <div className="text-sm font-medium dark:text-dark-text">{el.label}</div>
                    </div>
                    <div className="flex items-end gap-1 h-20">
                      {ratingsWithDates.map((item, idx) => {
                        const emojiIndex = state.ratingTracking.emojiSet.indexOf(item.rating);
                        const maxIndex = state.ratingTracking.emojiSet.length - 1;
                        const height = emojiIndex >= 0 ? ((emojiIndex + 1) / (maxIndex + 1)) * 100 : 0;
                        return (
                          <div
                            key={`${item.date}-${idx}`}
                            className="flex-1 flex flex-col items-center justify-end"
                            title={`${new Date(item.date).toLocaleDateString()}: ${item.rating}`}
                          >
                            <div
                              className="w-full bg-blue-500 dark:bg-blue-400 rounded-t transition-all"
                              style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                            />
                            <div className="text-xs mt-1 text-gray-500 dark:text-dark-textSecondary">
                              {item.rating}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-dark-textSecondary">
                      {ratingsWithDates.length} rating{ratingsWithDates.length !== 1 ? 's' : ''} over {recent.length} day{recent.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="mt-6 mb-24 text-xs text-gray-500 dark:text-dark-textSecondary">Tip: The insights panel will show richer visuals as you log more days.</div>
      </div>
    </div>
  );
}

export default Insights;
